import React, { Fragment, useEffect } from 'react'
import PropTypes from 'prop-types'
import RetailerFeature from '../../components/retailer-feature'
import PageLanding from '../../components/page-landing'
import RetailerUtilityBox from '../../components/retailer-utility-box'
import pageLandingStyle from '../../components/page-landing/index.scss'
import withStore from '../../containers/with-store'
import withModules from '../../containers/with-modules'
import Markdown from '../../components/markdown'
import { parseHostname } from '../../helpers/url'
import { occurrencesType, centreType, imageType } from '../../types'
import whatsHappeningModule from '../../helpers/whats-happening-module'
import pagesHoc from '../pages-hoc'
import Modules from '../../components/modules'
import getStoreStructuredData from '../../helpers/get-store-structured-data'
import getEntryOpenGraphData from '../../helpers/get-entry-open-graph-data'
import pageLoadDataLayer, {
  mapStoreToPageLoadData,
} from '../../helpers/analytics-utils/page-load-data-layer'
import getStoreMapLink from '../../helpers/get-store-map-link'
import { PromotionPositionContext } from '../../context'

const Store = ({ store, centre, template, modules }) => {
  const { openAt } = store
  const locationLink = getStoreMapLink(centre, store)
  const whatsHappeningEntries = store.happeningsInStore
  useEffect(() => {
    pageLoadDataLayer(mapStoreToPageLoadData(store), centre)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <Fragment>
      <PromotionPositionContext.Provider
        value={template?.analyticsMetadata?.displayId}
      >
        <PageLanding
          desktopImage={store.desktopImage}
          mobileImage={store.mobileImage}
          logo={store.logo.src}
          imageDataTestId="store-image"
          analyticsMetadata={store.analyticsMetadata}
        >
          <RetailerFeature
            className={pageLandingStyle.contentWrapper}
            title={store.title}
            body={
              <Markdown
                content={store.summary}
                dataTestId="store-description"
              />
            }
            offerings={store.offerings}
            services={store.services}
            callsToAction={store.callsToAction}
          />
          <div className={pageLandingStyle.sideBar}>
            <RetailerUtilityBox
              heading="Visit us"
              phoneNumber={store.phone}
              link={store.url}
              linkText={parseHostname(store.url)}
              locationLink={locationLink}
              status={store.status}
              openHours={store.openHours}
              location={store.location}
              nearestLocation={store.nearestLocation}
              storeImage={store.storeFrontImage}
              parkingLocation={store.parkingLocation}
              currentDay={1}
              openAt={openAt}
            />
          </div>
        </PageLanding>
      </PromotionPositionContext.Provider>
      <Modules
        modules={[
          ...(whatsHappeningEntries.length
            ? [
                whatsHappeningModule(
                  whatsHappeningEntries,
                  store.title,
                  centre,
                  template?.analyticsMetadata
                ),
              ]
            : []),
          ...modules,
        ]}
      />
    </Fragment>
  )
}

Store.propTypes = {
  store: PropTypes.shape({
    summary: PropTypes.string,
    desktopImage: imageType.isRequired,
    mobileImage: imageType.isRequired,
    logo: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
    phone: PropTypes.string,
    url: PropTypes.string,
    title: PropTypes.string,
    nearestLocation: PropTypes.string,
    storeImage: PropTypes.string,
    status: PropTypes.string,
    openHours: PropTypes.shape({
      storeTradingSchedule: PropTypes.shape({
        tradingHours: PropTypes.array.isRequired,
        isStoreCentreLinked: PropTypes.bool.isRequired,
      }),
      centreTradingSchedule: PropTypes.shape({
        tradingHours: PropTypes.array.isRequired,
      }),
      timeZone: PropTypes.string.isRequired,
    }).isRequired,
    storeFrontImage: PropTypes.string,
    openAt: PropTypes.string,
    offerings: PropTypes.array,
    services: PropTypes.array,
    callsToAction: PropTypes.array,
    location: PropTypes.string,
    parkingLocation: PropTypes.string,
    happeningsInStore: PropTypes.arrayOf(
      PropTypes.shape({
        link: PropTypes.string,
        heading: PropTypes.string,
        category: PropTypes.string,
        body: PropTypes.string,
        image: imageType.isRequired,
        occurrences: occurrencesType,
        location: PropTypes.string,
        storeIds: PropTypes.array,
      })
    ),
    analyticsMetadata: PropTypes.shape({
      correlationId: PropTypes.string,
      title: PropTypes.string,
      promotion: PropTypes.shape({
        creative: PropTypes.string,
      }),
    }),
    paymentMethods: PropTypes.arrayOf(PropTypes.string),
  }),
  centre: centreType,
  template: PropTypes.shape({
    analyticsMetadata: PropTypes.shape({
      displayId: PropTypes.string,
    }),
  }),
  modules: PropTypes.array,
}

const getMetaData = ({ centre, store, canonical }) => {
  const title = `${store.title} at Westfield ${centre.title}`
  const description = `Westfield ${centre.title} ${store.title} - Check opening hours, phone number, address & directions. Find 100s of stores like ${store.title} & more at Westfield ${centre.title}!`
  const url = canonical
  return {
    title,
    description,
    openGraphData: getEntryOpenGraphData({
      entry: store,
      url,
    }),
    schema: getStoreStructuredData({
      url,
      title,
      description,
      store,
      country: centre.country,
    }),
  }
}

export default withStore(withModules(pagesHoc(Store, getMetaData)))
